import { PlusOutlined } from '@ant-design/icons'
import { Image, Page, Text, View } from '@react-pdf/renderer'
import { Button, Empty, Form, Space, Typography } from 'antd'
import { PageFooter } from 'features/reporting/pdfGeneration/components/shared/PageFooter'
import { PageHeader } from 'features/reporting/pdfGeneration/components/shared/PageHeader'
import { PageSubheader } from 'features/reporting/pdfGeneration/components/shared/PageSubHeader'
import { PDFStyles } from 'features/reporting/pdfGeneration/styles/PDFStyles'
import { PdfProps, ReportSection } from 'features/reporting/types'
import { ClientProvidedImagesData } from 'pages/reporting/reports/raut/rautReportData'
import { useRautReportOps } from 'pages/reporting/reports/raut/RautReportProvider'
import { ClientImageField } from 'pages/reporting/reports/raut/sections/subSections/clientProvidedImagesComponents/ClientImageField'
import { StyledForm } from 'pages/reporting/shared/StyledComponents'
import React, { useMemo, useRef } from 'react'
import styled from 'styled-components'

const { Text: AntText } = Typography

const PageContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`

const ImagesContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`

const EmptyStateContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 3rem 1.5rem;
  background: ${(props) => props.theme.forecast.cardGroupBackground};
  border-radius: 0.5rem;
  border: 1px dashed ${(props) => props.theme.forecast.cardDividerBackground};
`

const AddImageButton = styled(Button)`
  width: 100%;
  height: 2.75rem;
  border-style: dashed;
  border-radius: 0.5rem;
  font-weight: 500;
  transition: all 0.2s ease;

  &:hover:not(:disabled) {
    border-style: dashed;
  }
`

const RenderReact: React.FC = () => {
  const {
    formData,
    metadataEntryId,
    handleClientProvidedImagesFinish,
    saveClientProvidedImagesSilent,
    registerPageSaveHandler,
  } = useRautReportOps()

  const initialValues = useMemo(
    () => formData.clientProvidedImagesData,
    [formData.clientProvidedImagesData]
  )

  const [form] = Form.useForm<ClientProvidedImagesData>()

  const handleAddImage = (add: (defaultValue?: object) => void) => {
    if (!formData.lockState?.isLocked) {
      add({
        id: generateUniqueId(),
        type: 'drawing',
      })
    }
  }

  const saveBeforeNavigation = React.useCallback(async () => {
    const values = form.getFieldsValue(true) as ClientProvidedImagesData
    await saveClientProvidedImagesSilent(values)
  }, [form, saveClientProvidedImagesSilent])

  React.useEffect(() => {
    registerPageSaveHandler('clientImages', saveBeforeNavigation)

    return () => {
      registerPageSaveHandler('clientImages', null)
    }
  }, [registerPageSaveHandler, saveBeforeNavigation])

  return (
    <StyledForm
      form={form}
      layout='vertical'
      onFinish={handleClientProvidedImagesFinish}
      initialValues={initialValues}
      disabled={formData.lockState?.isLocked}>
      <PageContainer>
        <Form.List name='images'>
          {(fields, { add, remove }) => (
            <ImagesContainer>
              {fields.length === 0 ? (
                <EmptyStateContainer>
                  <Empty
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                    description={
                      <Space direction='vertical' size={4}>
                        <AntText type='secondary'>No images added yet</AntText>
                        <AntText
                          type='secondary'
                          style={{ fontSize: '0.75rem' }}>
                          Add client-provided images to include in the report
                        </AntText>
                      </Space>
                    }
                  />
                </EmptyStateContainer>
              ) : (
                fields.map((field, index) => (
                  <ClientImageField
                    key={field.key}
                    field={field}
                    index={index}
                    metadataEntryId={metadataEntryId}
                    onRemove={() =>
                      !formData.lockState?.isLocked && remove(field.name)
                    }
                    form={form}
                  />
                ))
              )}

              <Form.Item style={{ marginBottom: 0 }}>
                <AddImageButton
                  type='dashed'
                  onClick={() => handleAddImage(add)}
                  icon={<PlusOutlined />}>
                  Add Client Image
                </AddImageButton>
              </Form.Item>
            </ImagesContainer>
          )}
        </Form.List>
      </PageContainer>
    </StyledForm>
  )
}

export const generateUniqueId = () => {
  return `img_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
}

const RenderPDF: React.FC<PdfProps> = ({ onRender }) => {
  const { formData, clientImages } = useRautReportOps()

  const hasRegisteredFirstPage = useRef(false)

  // Check if we have any images with signed URLs
  const hasImages = clientImages.some((img) => img.signedUrl)

  if (!hasImages) {
    // If there are no images, return a single page with a message
    return (
      <Page size='A4' style={PDFStyles.page} id='clientImages'>
        <Text
          render={({ pageNumber }) => {
            onRender({ id: 'clientImages', pageNumber })
            return ''
          }}
        />

        <PageHeader title='Client Provided Images' />

        <View style={PDFStyles.content}>
          <Text style={PDFStyles.noImageMessage}>
            No client provided images available
          </Text>
        </View>

        <PageFooter
          jobNumber={formData.coverPageData.geckoJobId}
          revisionNumber={formData.revisionMetadata.revisionNumber}
          prelim={
            !(
              formData.signoff?.reviewerSignoff &&
              formData.signoff.supervisorSignoff
            )
          }
        />
      </Page>
    )
  }

  // If we have images, render one per page
  return (
    <>
      {clientImages
        .filter((image) => image.signedUrl) // Only include images with a signed URL
        .map((image, index) => (
          <Page
            key={`client-image-${index}`}
            size='A4'
            orientation='landscape'
            style={PDFStyles.page}
            // Use 'clientImages' for the first page, and unique IDs for subsequent pages
            id={index === 0 ? 'clientImages' : `clientImage${index + 1}`}
            wrap={false}>
            {/* Hidden text for page tracking */}
            <Text
              render={({ pageNumber }) => {
                // Register only the first page with 'clientImages' ID for the TOC
                if (index === 0 && !hasRegisteredFirstPage.current) {
                  onRender({ id: 'clientImages', pageNumber })
                  hasRegisteredFirstPage.current = true
                }
                return ''
              }}
            />
            <PageHeader title='Client Provided Images' />
            <View
              style={{
                margin: '10px 10px 10px',
                paddingBottom: 45,
                flexGrow: 1,
                display: 'flex',
                flexDirection: 'column',
              }}>
              <View style={{ alignItems: 'flex-start', flexShrink: 0 }}>
                <PageSubheader
                  title={`Client Image ${index + 1}`}
                  align='left'
                  color='black'
                />
              </View>

              {/* User's caption/description */}
              {image.imageCaption && (
                <View style={{ flexShrink: 0 }}>
                  <Text style={PDFStyles.imageCaption}>
                    {image.imageCaption}
                  </Text>
                </View>
              )}

              <View
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexGrow: 1,
                  flexShrink: 1,
                  maxHeight: '450px',
                  marginTop: 10,
                }}>
                {image.signedUrl && (
                  <Image
                    src={image.signedUrl}
                    style={{
                      maxWidth: 600,
                      maxHeight: 400,
                    }}
                  />
                )}
              </View>

              {/* Customer supplied disclaimer */}
              <View style={{ flexShrink: 0, marginTop: 10 }}>
                <Text style={PDFStyles.imageDescription}>
                  Image provided by client for reference
                </Text>
              </View>
            </View>
            <PageFooter
              jobNumber={formData.coverPageData.geckoJobId}
              revisionNumber={formData.revisionMetadata.revisionNumber}
            />{' '}
          </Page>
        ))}
    </>
  )
}

export const ClientImages: ReportSection = {
  RenderReact,
  RenderPDF,
  required: true,
  id: 'clientImages',
  description: 'Upload and manage client provided images',
  title: 'Client Images',
  requiredFields: [],
}
